import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CaretRight, CheckCircle } from "@phosphor-icons/react";
import { WorkStaleChip } from "./WorkItemsStyled";
import { radius, spacing, status, typography } from "../../theme/tokens";

/**
 * The handle for a document's open work item when the right-hand panel
 * is closed. Sits in the document toolbar row — the row already exists,
 * so it costs the PDF no height (Matthew, 2026-10-27: "I don't like
 * anything that reduces the height of a pdf"). One click opens the
 * panel, where the work item sits beside the document so the reader
 * can see the request, the pages, and their own note at once.
 *
 * Renders nothing when the document belongs to no open work item.
 */
const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  height: 30px;
  max-width: 320px;
  padding: 0 ${spacing[3]};
  border-radius: ${radius.md};
  border: 1px solid ${status.ready.border};
  background: ${status.ready.bg};
  color: ${status.ready.fg};
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.medium};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 1;
  min-width: 0;

  &:hover {
    filter: brightness(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${status.ready.fg};
    outline-offset: 1px;
  }
`;

const PillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const PillChip = styled(WorkStaleChip)`
  margin-right: 0;
`;

const DocumentWorkItemPill = ({ items = [], onOpen }) => {
  if (!items.length) return null;

  const [first] = items;
  const label =
    items.length === 1
      ? first.subject || first.itemNumber || "Work item"
      : `${items.length} work items`;
  const chip = first.staleness ? (
    <PillChip $tone={first.staleness}>
      {first.staleLabel || `${first.ageDays}d`}
    </PillChip>
  ) : null;

  return (
    <PillButton
      type="button"
      onClick={onOpen}
      data-testid="document-work-item-pill"
      aria-label={`Open the work item on this document: ${label}`}
      title="Show the work item beside this document"
    >
      <CheckCircle size={14} weight="bold" aria-hidden="true" />
      <PillLabel>
        {items.length === 1 ? "Work item: " : ""}
        {label}
      </PillLabel>
      {items.length === 1 && chip}
      <CaretRight size={12} weight="bold" aria-hidden="true" />
    </PillButton>
  );
};

DocumentWorkItemPill.propTypes = {
  /** Rows from the waiting-on endpoint's `linked` list — the open work
   * items this document belongs to. */
  items: PropTypes.arrayOf(PropTypes.object),
  /** Opens the right-hand panel on the work item. */
  onOpen: PropTypes.func.isRequired,
};

export default DocumentWorkItemPill;
